// Smart initialization that preserves theme data
(function() {
  'use strict';
  
  // Check if we need to initialize or just reload functions
  const isFirstLoad = !window.wickProProgram || !window.wickProProgram.initialized;

  // Initialize or extend the namespace
  if (!window.wickProProgram) {
    window.wickProProgram = {};
  }

  // Preserve existing theme data and extend with new properties
  Object.assign(window.wickProProgram, {
    initialized: true,
    
    // Data containers (don't overwrite if they exist)
    customerData: window.wickProProgram.customerData || {
      isProCustomer: false,
      proLevel: null,
      proExpiration: null,
      isProActive: false
    },
    
    proLevels: window.wickProProgram.proLevels || [],
    productAdjustments: window.wickProProgram.productAdjustments || {},
    originalPrices: window.wickProProgram.originalPrices || new Map(),
    
    // Configuration
    config: {
      version: '1.0.0',
      debug: false, // Enable debug logging
      // CSS selectors for your specific theme structure
      priceSelectors: {
        current: 'sale-price, .sale-price, .price, .product-price, [data-price], .price-item, .money, .current-price',
        compare: 'compare-at-price, .compare-at-price, .original-price, .was-price, .line-through', 
        container: 'price-list, .price-list, [data-block-type="price"], .product-price-container, .price-container',
        // Update these selectors to match your theme
        productCard: 'product-card, .product-card, .card-wrapper, .product-item, .grid-product, .product-grid-item, [data-product]',
        productCardPrice: 'product-card sale-price, product-card .price, .product-card .price, .card-wrapper .price, .product-item .price, .grid-product .price'
      },
      
      // CSS classes for styling
      classes: {
        proPrice: 'pro-price',
        proBadge: 'pro-badge',
        originalPrice: 'price--original',
        processed: 'pro-processed'
      }
    },

    // Badge settings from theme customizer (will be populated from settings_schema.json)
    badgeSettings: window.wickProProgram.badgeSettings || {
      enabled: true,
      showLevel: true,
      showExpiration: true,
      backgroundColor: '#2c5aa0',
      textColor: '#ffffff',
      fontSize: '12px',
      padding: '4px 8px',
      borderRadius: '4px',
      position: 'after-price', // 'after-price', 'before-price', 'above-product'
      text: 'PRO PRICING',
      customText: ''
    },
    
    // Utility functions
    utils: {
      /**
       * Parse price from text
       */
      parsePrice: function(priceText) {
        if (!priceText) return 0;
        const cleaned = priceText.toString().replace(/[^0-9.]/g, '');
        return parseFloat(cleaned) || 0;
      },
      
      /**
       * Format price for display
       */
      formatPrice: function(price) {
        try {
          const currency = window.Shopify?.currency?.active || 'USD';
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
          }).format(price / 100);
        } catch (error) {
          // Fallback formatting
          return '$' + (price / 100).toFixed(2);
        }
      },
      
      /**
       * Check if a date has expired
       */
      isExpired: function(dateString) {
        if (!dateString) return false;
        
        try {
          const now = Math.floor(Date.now() / 1000);
          const expirationTimestamp = Math.floor(new Date(dateString).getTime() / 1000);
          
          return expirationTimestamp < now;
        } catch (error) {
          console.error('Error parsing date:', error);
          return false;
        }
      },
      
      /**
       * Debug logger
       */
      log: function(...args) {
        if (window.wickProProgram.config.debug) {
          // console.log('[Wick Pro Program]', ...args);
        }
      },
      
      /**
       * Normalize level names for consistent comparison
       */
      normalizeLevelName: function(levelName) {
        return (levelName || '').toLowerCase().trim();
      },
      

      /**
       * Generate unique selector for price elements
       */
      getPriceElementId: function(element, prefix) {
        // For product pages, scope to product-info to avoid cart interference
        const isProductPage = window.location.pathname.includes('/products/') || 
                             document.body.classList.contains('template-product');
        
        let priceScope = document;
        if (isProductPage) {
          priceScope = document.querySelector('.product-info') || 
                      document.querySelector('[data-block-type="price"]') ||
                      document.querySelector('.product-form') ||
                      document.querySelector('.product-details') ||
                      document;
        }
        
        const allElements = Array.from(priceScope.querySelectorAll(window.wickProProgram.config.priceSelectors.current));
        return `${prefix}-${allElements.indexOf(element)}`;
      }

    }
  });

  // Configuration shortcuts
  const CONFIG = window.wickProProgram.config;
  const utils = window.wickProProgram.utils;
  const badgeSettings = window.wickProProgram.badgeSettings;

  // Data shortcuts
  let customerData = window.wickProProgram.customerData;
  let originalPrices = window.wickProProgram.originalPrices;
  let proLevels = window.wickProProgram.proLevels;
  let productAdjustments = window.wickProProgram.productAdjustments;

  /**
   * Load badge settings from theme customizer
   */
  function loadBadgeSettings() {
    // These will be populated from settings_schema.json via theme liquid
    if (window.wickProProgram?.badgeSettingsFromTheme) {
      Object.assign(badgeSettings, window.wickProProgram.badgeSettingsFromTheme);
      // console.log('Badge settings loaded from theme:', badgeSettings);
    }
  }
  
  /**
   * Create Pro badge element with customizable styling
   */
  function createProBadge() {
    const badge = document.createElement('div');
    badge.className = CONFIG.classes.proBadge;
    
    // Apply custom styles from settings
    const styles = {
      display: 'inline-block',
      backgroundColor: badgeSettings.backgroundColor,
      color: badgeSettings.textColor,
      padding: badgeSettings.padding,
      borderRadius: badgeSettings.borderRadius,
      fontSize: badgeSettings.fontSize,
      fontWeight: 'bold',
      margin: badgeSettings.margin,
      letterSpacing: badgeSettings.letterSpacing
    };
    
    Object.assign(badge.style, styles);
    
    // Build badge content
    let badgeText = badgeSettings.customText || badgeSettings.text;
    
    if (badgeSettings.showLevel && customerData.proLevel) {
      badgeText = badgeText.replace(/PRO/g, `PRO ${customerData.proLevel.toUpperCase()}`);
    }
    
    let badgeContent = `<span>${badgeText}</span>`;
    
    // Add expiration if enabled and available
    if (badgeSettings.showExpiration && customerData.proExpiration) {
      const expirationDate = new Date(customerData.proExpiration);
      badgeContent += `
        <span style="
          display: block;
          font-size: ${parseInt(badgeSettings.fontSize) - 2}px;
          opacity: 0.8;
          margin-top: 2px;
        ">
          Valid until ${expirationDate.toLocaleDateString()}
        </span>
      `;
    }
    
    badge.innerHTML = badgeContent;
    return badge;
  }
  
  /**
   * Process collection page with multiple product cards
   */
  function processCollectionPage() {
    const productCards = document.querySelectorAll(CONFIG.priceSelectors.productCard);
    // console.log(`Found ${productCards.length} product cards to process`);
    
    productCards.forEach((card, index) => {
      // Skip if already processed
      if (card.classList.contains(CONFIG.classes.processed)) {
        return;
      }
      
      // console.log(`Processing product card ${index + 1}`);

      // Try to extract product ID from the card
      // Common patterns: data-product-id, data-product, or href containing /products/handle
      let productId = card.dataset.productId || card.dataset.product;
      
      if (!productId) {
        // Try to find it in a link within the card
        const productLink = card.querySelector('a[href*="/products/"]');
        if (productLink) {
          const href = productLink.getAttribute('href');
          const handleMatch = href.match(/\/products\/([^/?]+)/);
          if (handleMatch) {
            const handle = handleMatch[1];
            // Try to find product ID from collectionProductData by handle
            if (window.wickProProgram?.collectionProductData) {
              for (const [id, data] of Object.entries(window.wickProProgram.collectionProductData)) {
                if (data.handle === handle) {
                  productId = id;
                  break;
                }
              }
            }
          }
        }
      }
      
      // Store product ID on the card for later reference
      if (productId) {
        card.dataset.wickProductId = productId;
        // console.log(`Found product ID ${productId} for card ${index + 1}`);
      }
      
      // Find price elements within this card
      const priceElements = card.querySelectorAll(CONFIG.priceSelectors.current);
      const comparePriceElements = card.querySelectorAll(CONFIG.priceSelectors.compare);
      
      // console.log(`Found ${priceElements.length} price elements and ${comparePriceElements.length} compare price elements in card ${index + 1}`);
      
      // Store original prices for this card
      storeOriginalPricesForCard(card, priceElements, comparePriceElements);
      
      // Update pricing for this card (now with product ID)
      updateProPricingForCard(card, priceElements, comparePriceElements, productId);
      
      // Add badge to this card (now with product ID)
      addProBadgeToCard(card, productId);
      
      // Mark as processed
      card.classList.add(CONFIG.classes.processed);
    });
  }
  
  /**
   * Store original prices for a specific card
   */
  function storeOriginalPricesForCard(card, priceElements, comparePriceElements) {
    priceElements.forEach((element, index) => {
      // Try to get price from data attribute first, then text content
      let price = parseFloat(element.dataset.originalPrice);
      
      if (!price || isNaN(price)) {
        // For custom elements like <sale-price>, get the text content excluding hidden spans
        let priceText = element.textContent.trim();
        
        // Remove "Sale price" or "Regular price" screen reader text
        priceText = priceText.replace(/^(Sale price|Regular price)\s*/i, '');
        
        price = utils.parsePrice(priceText);
        // console.log(`Extracted price from card element ${index}: "${priceText}" -> ${price}`);
      }
      
      if (price > 0) {
        const elementId = `card-${Array.from(document.querySelectorAll(CONFIG.priceSelectors.productCard)).indexOf(card)}-current-${index}`;
        originalPrices.set(elementId, price);
        element.dataset.originalPrice = price;
        element.dataset.priceId = elementId;
        // console.log(`Stored price for card element ${elementId}:`, price);
      }
    });
  
    comparePriceElements.forEach((element, index) => {
      let price = parseFloat(element.dataset.originalComparePrice);
      
      if (!price || isNaN(price)) {
        let priceText = element.textContent.trim();
        
        // Remove "Regular price" screen reader text
        priceText = priceText.replace(/^(Regular price|Compare at)\s*/i, '');
        
        // Skip if it's $0 or empty
        const parsedPrice = utils.parsePrice(priceText);
        if (parsedPrice > 0) {
          price = parsedPrice;
          // console.log(`Extracted compare price from card element ${index}: "${priceText}" -> ${price}`);
        }
      }
      
      if (price > 0) {
        const elementId = `card-${Array.from(document.querySelectorAll(CONFIG.priceSelectors.productCard)).indexOf(card)}-compare-${index}`;
        originalPrices.set(elementId, price);
        element.dataset.originalComparePrice = price;
        element.dataset.priceId = elementId;
        // console.log(`Stored compare price for card element ${elementId}:`, price);
      }
    });
  }
  
  /**
   * Update Pro pricing for a specific card
   */
  function updateProPricingForCard(card, priceElements, comparePriceElements, productId = null) {
    priceElements.forEach((element, index) => {
      const elementId = element.dataset.priceId;
      const originalPrice = parseFloat(element.dataset.originalPrice) || originalPrices.get(elementId);
      
      // Get compare at price if available
      const compareElement = comparePriceElements[index];
      let originalComparePrice = originalPrice;
      
      if (compareElement) {
        const compareElementId = compareElement.dataset.priceId;
        originalComparePrice = parseFloat(compareElement.dataset.originalComparePrice) || 
                             originalPrices.get(compareElementId) || 
                             originalPrice;
      }
      
      // console.log(`Processing card element ${index}: original=${originalPrice}, compare=${originalComparePrice}, productId=${productId}`);
      
      if (originalPrice > 0) {
        // Pass productId to calculateProDiscount so it can look up the correct adjustments
        const result = calculateProDiscount(originalPrice, originalComparePrice, productId);
        
        // Determine if there's a sale happening (any discount from compare price)
        const isOnSale = result.discount > 0 || originalComparePrice > originalPrice;
        const finalDisplayPrice = result.discount > 0 ? result.discountedPrice : originalPrice;
        
        // For custom elements, we need to update the text content while preserving screen reader text
        const srText = element.querySelector('.sr-only')?.textContent || '';
        const newPriceText = utils.formatPrice(finalDisplayPrice * 100);
        
        if (srText) {
          element.innerHTML = `<span class="sr-only">${srText}</span>${newPriceText}`;
        } else {
          element.textContent = newPriceText;
        }
        
        // Manage the text-on-sale class
        if (isOnSale) {
          // Add text-on-sale class when there's any kind of sale
          element.classList.add('text-on-sale');
          if (result.discount > 0) {
            element.classList.add(CONFIG.classes.proPrice);
          }
        } else {
          // Remove text-on-sale class when there's no sale
          element.classList.remove('text-on-sale');
          element.classList.remove(CONFIG.classes.proPrice);
        }
        
        // Handle compare-at-price visibility and content
        if (compareElement) {
          if (isOnSale) {
            // Show compare price when there's any sale
            compareElement.removeAttribute('hidden');
            
            // Determine what price to show as compare-at
            let compareDisplayPrice = originalComparePrice;
            
            // If Pro discount is applied, show the higher of original or compare price
            if (result.discount > 0) {
              compareDisplayPrice = Math.max(originalPrice, originalComparePrice);
            }
            
            // Update compare price text
            const compareSrText = compareElement.querySelector('.sr-only')?.textContent || '';
            const comparePriceText = utils.formatPrice(compareDisplayPrice * 100);
            
            if (compareSrText) {
              compareElement.innerHTML = `<span class="sr-only">${compareSrText}</span>${comparePriceText}`;
            } else {
              compareElement.textContent = comparePriceText;
            }
            
            // console.log(`Showing compare price: ${comparePriceText}`);
          } else {
            // Hide compare price when there's no sale
            compareElement.setAttribute('hidden', '');
            // console.log(`Hiding compare price - no sale occurring`);
          }
        }
        
        if (result.discount > 0) {
          // console.log(`Successfully applied Pro discount: ${result.discount}% - Final price: ${newPriceText}`);
        } else if (originalComparePrice > originalPrice) {
          // console.log(`No Pro discount, but regular sale maintained - Price: ${newPriceText}`);
        } else {
          // console.log(`No discount applied - Regular price: ${newPriceText}`);
        }
      } else {
        // console.log(`Skipping card element ${index} - no valid original price`);
      }
    });
  }
  
  /**
   * Add Pro badge to a specific card
   */
  function addProBadgeToCard(card, productId = null) {
    // Only add badge if enabled and customer is active Pro
    if (!badgeSettings.enabled || !customerData.isProActive) {
      // console.log('Badge disabled or customer not active pro');
      return;
    }

    // Check if customer is getting a discount for this specific card
    if (!isCustomerGettingDiscountForCard(card, productId)) {
      console.log('No discount for this card, not showing badge');
      return;
    }
  
    // Don't add if badge already exists
    if (card.querySelector(`.${CONFIG.classes.proBadge}`)) {
      // console.log('Badge already exists for this card');
      return;
    }
  
    // Find price container within this card - updated for your theme
    let priceContainer = card.querySelector('price-list') ||
                        card.querySelector('.price-list') ||
                        card.querySelector(CONFIG.priceSelectors.container) ||
                        card.querySelector('.price') ||
                        card.querySelector('.product-price') ||
                        card.querySelector('[data-price]');
  
    if (!priceContainer) {
      // Fallback: create container after first price element
      const firstPrice = card.querySelector('sale-price') || 
                        card.querySelector(CONFIG.priceSelectors.current);
      if (firstPrice) {
        priceContainer = firstPrice.parentElement;
        // console.log('Using fallback price container:', priceContainer);
      }
    }
  
    // console.log('Price container found:', priceContainer);
  
    if (priceContainer) {
      const badge = createProBadge();
      // console.log('Created badge:', badge);
      
      // Position badge based on settings
      if (badgeSettings.position === 'before-price') {
        priceContainer.insertBefore(badge, priceContainer.firstChild);
      } else if (badgeSettings.position === 'above-product') {
        // Find product title or image and insert before it
        const productTitle = card.querySelector('.product-title, .card-title, h3, h2, .h5') ||
                            card.querySelector('img');
        if (productTitle) {
          productTitle.parentElement.insertBefore(badge, productTitle);
        } else {
          priceContainer.appendChild(badge);
        }
      } else {
        // Default: after price
        priceContainer.appendChild(badge);
      }
      
      // console.log('Badge added to card');
    } else {
      // console.log('No price container found for badge placement');
    }
  }

  /**
   * Check if customer is actually getting a discount on the current product page
   */
  function isCustomerGettingDiscount() {
    if (!customerData.isProActive) {
      return false;
    }

    // Get the first price element to test
    const isProductPage = window.location.pathname.includes('/products/') || 
                        document.body.classList.contains('template-product');
    
    let priceScope = document;
    if (isProductPage) {
      priceScope = document.querySelector('.product-info') || 
                  document.querySelector('[data-block-type="price"]') ||
                  document.querySelector('.product-form') ||
                  document.querySelector('.product-details') ||
                  document;
    }
    
    const firstPriceElement = priceScope.querySelector(CONFIG.priceSelectors.current);
    if (!firstPriceElement) {
      return false;
    }

    // Get original price and compare price
    const originalPrice = parseFloat(firstPriceElement.dataset.originalPrice) || 0;
    if (originalPrice <= 0) {
      return false;
    }

    const comparePriceElements = priceScope.querySelectorAll(CONFIG.priceSelectors.compare);
    const compareElement = comparePriceElements[0];
    let originalComparePrice = originalPrice;
    
    if (compareElement) {
      originalComparePrice = parseFloat(compareElement.dataset.originalComparePrice) || originalPrice;
    }

    // Calculate discount and see if price actually changes
    const result = calculateProDiscount(originalPrice, originalComparePrice);
    
    // Return true if there's a Pro discount being applied
    return result.discount > 0;
  }

  /**
   * Check if customer is getting a discount for a specific card
   */
  function isCustomerGettingDiscountForCard(card, productId = null) {
    if (!customerData.isProActive) {
      return false;
    }

    // Get the first price element in this card to test
    const firstPriceElement = card.querySelector(CONFIG.priceSelectors.current);
    if (!firstPriceElement) {
      return false;
    }

    // Get original price
    const originalPrice = parseFloat(firstPriceElement.dataset.originalPrice) || 0;
    if (originalPrice <= 0) {
      return false;
    }

    // Get compare price if available
    const comparePriceElements = card.querySelectorAll(CONFIG.priceSelectors.compare);
    const compareElement = comparePriceElements[0];
    let originalComparePrice = originalPrice;
    
    if (compareElement) {
      originalComparePrice = parseFloat(compareElement.dataset.originalComparePrice) || originalPrice;
    }

    // Calculate discount with product ID so it uses the correct adjustments
    const result = calculateProDiscount(originalPrice, originalComparePrice, productId);
    
    // Return true if there's a Pro discount being applied
    return result.discount > 0;
  }
  
  /**
   * Initialize the Pro pricing system
   */
  async function init() {
    try {
      // console.log('Initializing Pro pricing system');
      
      // Load customer data
      await loadCustomerData();
      
      // Load pro levels and product adjustments
      await loadProLevelsData();
      await loadProductAdjustments();
      
      // Load badge settings from theme customizer
      loadBadgeSettings();
      
      // console.log('Data loaded. Customer:', customerData, 'Pro Levels:', proLevels);
      // console.log('Badge settings:', badgeSettings);
      
      // Only proceed if customer is a Pro customer AND their membership is active
      if (!customerData.isProCustomer || !customerData.isProActive) {
        if (!customerData.isProCustomer) {
          // console.log('Customer is not a Pro customer');
        } else {
          // console.log('Customer Pro membership has expired');
        }
        return;
      }
      
      // console.log('Customer is active Pro member, applying pricing');
      
      // Check if we're on a collection page
      const isCollectionPage = document.body.classList.contains('template-collection') ||
                              document.querySelector('.collection-products') ||
                              document.querySelector('.product-grid') ||
                              window.location.pathname.includes('/collections/');
      
      if (isCollectionPage) {
        // console.log('Collection page detected, processing product cards');
        processCollectionPage();
      } else {
        // console.log('Product page detected, processing single product');
        // Store original prices
        storeOriginalPrices();
        
        // Apply initial Pro pricing
        updateProPricing();
        
        // Add Pro badge
        addProBadge();
      }
      
      // console.log('Pro pricing system initialized successfully');
    } catch (error) {
      console.error('Error initializing Pro pricing:', error);
    }
  }

  /**
   * Load customer data from theme variables
   */
  async function loadCustomerData() {
    try {
      // console.log('Loading customer data');
      
      if (window.wickProProgram?.customerDataFromTheme) {
        const themeData = window.wickProProgram.customerDataFromTheme;
        
        const isProActive = themeData.isProCustomer && !utils.isExpired(themeData.proExpiration);

        customerData.isProCustomer = themeData.isProCustomer;
        customerData.proLevel = themeData.proLevel;
        customerData.proExpiration = themeData.proExpiration;
        customerData.isProActive = isProActive;

        // console.log('Customer data loaded:', customerData);
        return;
      }

      // console.log('No customer data found in theme');
    } catch (error) {
      console.error('Error loading customer data:', error);
    }
  }

  /**
   * Load pro levels data from shop metafields
   */
  async function loadProLevelsData() {
    try {
      if (window.wickProProgram?.proLevelsFromTheme) {
        if (typeof window.wickProProgram.proLevelsFromTheme === 'string') {
          proLevels = JSON.parse(window.wickProProgram.proLevelsFromTheme);
        } else {
          proLevels = window.wickProProgram.proLevelsFromTheme;
        }
        window.wickProProgram.proLevels = proLevels;
        // console.log('Pro levels loaded:', proLevels);
        return;
      }

      // console.log('No pro levels data found');
    } catch (error) {
      console.error('Error loading pro levels:', error);
      proLevels = [];
    }
  }

  /**
   * Load product-specific adjustments
   */
  async function loadProductAdjustments() {
    try {
      if (window.wickProProgram?.productAdjustmentsFromTheme) {
        if (typeof window.wickProProgram.productAdjustmentsFromTheme === 'string') {
          productAdjustments = JSON.parse(window.wickProProgram.productAdjustmentsFromTheme);
        } else {
          productAdjustments = window.wickProProgram.productAdjustmentsFromTheme;
        }
        window.wickProProgram.productAdjustments = productAdjustments;
        // console.log('Product adjustments loaded:', productAdjustments);
      } else {
        // console.log('No product adjustments found');
        productAdjustments = {};
      }
      
      // Also load collection product adjustments if available
      if (window.wickProProgram?.collectionProductAdjustments) {
        window.wickProProgram.collectionProductAdjustmentsMap = window.wickProProgram.collectionProductAdjustments;
        // console.log('Collection product adjustments loaded:', window.wickProProgram.collectionProductAdjustmentsMap);
      }
    } catch (error) {
      console.error('Error loading product adjustments:', error);
      productAdjustments = {};
    }
  }

  /**
   * Store original prices for calculations
   */
  function storeOriginalPrices() {
    // console.log('Storing original prices');
    
    // For product pages, scope to product-info area to avoid cart interference
    const isProductPage = window.location.pathname.includes('/products/') || 
                         document.body.classList.contains('template-product');
    
    let priceScope = document;
    if (isProductPage) {
      // Look for product info container first
      priceScope = document.querySelector('.product-info');
      // console.log('Product page detected, scoping to:', priceScope);
    }
    
    const priceElements = priceScope.querySelectorAll(CONFIG.priceSelectors.current);
    // console.log('Found price elements:', priceElements);
    
    priceElements.forEach((element, index) => {
      // Try to get price from data attribute first, then text content
      let price = parseFloat(element.dataset.originalPrice);
      
      if (!price || isNaN(price)) {
        // For custom elements like <sale-price>, get the text content excluding hidden spans
        let priceText = element.textContent.trim();
        
        // Remove "Sale price" or "Regular price" screen reader text
        priceText = priceText.replace(/^(Sale price|Regular price)\s*/i, '');
        
        price = utils.parsePrice(priceText);
        // console.log(`Extracted price from element ${index}: "${priceText}" -> ${price}`);
      }
      
      if (price > 0) {
        const elementId = utils.getPriceElementId(element, 'current');
        originalPrices.set(elementId, price);
        element.dataset.originalPrice = price;
        // console.log(`Stored price for element ${elementId}:`, price);
      }
    });
  
    const comparePriceElements = priceScope.querySelectorAll(CONFIG.priceSelectors.compare);
    // console.log('Found compare price elements:', comparePriceElements);
    
    comparePriceElements.forEach((element, index) => {
      let price = parseFloat(element.dataset.originalComparePrice);
      
      if (!price || isNaN(price)) {
        // For hidden elements or elements showing $0, try to get from variant data
        if (window.wickProProgram?.productData?.variants) {
          // Try to get current variant's compare_at_price
          const currentVariantId = getCurrentVariantId();
          if (currentVariantId) {
            const variant = window.wickProProgram.productData.variants.find(v => v.id == currentVariantId);
            if (variant && variant.compare_at_price > 0) {
              price = variant.compare_at_price / 100; // Convert from cents
              // console.log(`Got compare price from variant data: ${price}`);
            }
          }
        }
        
        // Fallback: try to parse text content
        if (!price || isNaN(price)) {
          let priceText = element.textContent.trim();
          
          // Remove "Regular price" screen reader text
          priceText = priceText.replace(/^(Regular price|Compare at)\s*/i, '');
          
          // Skip if it's $0 or empty
          const parsedPrice = utils.parsePrice(priceText);
          if (parsedPrice > 0) {
            price = parsedPrice;
            // console.log(`Extracted compare price from element ${index}: "${priceText}" -> ${price}`);
          }
        }
      }
      
      if (price > 0) {
        const elementId = utils.getPriceElementId(element, 'compare');
        originalPrices.set(elementId, price);
        element.dataset.originalComparePrice = price;
        // console.log(`Stored compare price for element ${elementId}:`, price);
      }
    });
  }
  
  /**
   * Helper function to get current variant ID
   */
  function getCurrentVariantId() {
    // Try multiple ways to get current variant
    const variantInput = document.querySelector('[name="id"]');
    if (variantInput) {
      return variantInput.value;
    }
    
    // Fallback: get first variant if available
    if (window.wickProProgram?.productData?.variants?.length > 0) {
      return window.wickProProgram.productData.variants[0].id;
    }
    
    return null;
  }

  // Calculate the ProDiscount with all parameters
  function calculateProDiscount(basePrice, compareAtPrice, productId = null) {
    if (!customerData.isProCustomer || !customerData.isProActive || !customerData.proLevel) {
      return { discountedPrice: basePrice, discount: 0 };
    }

    const price = parseFloat(basePrice) || 0;
    const compareAt = parseFloat(compareAtPrice) || price;

    // Find the base discount % for the customer's level
    const normalizedProLevel = utils.normalizeLevelName(customerData.proLevel);
    let proDiscount = 0;

    for (const level of proLevels) {
      const levelName = utils.normalizeLevelName(level.name);
      if (levelName === normalizedProLevel) {
        proDiscount = level.discount;
        break;
      }
    }

    // Handle per-product adjustments
    // If productId is provided (collection page), use collectionProductAdjustmentsMap
    // Otherwise use productAdjustments (product page)
    let adjustmentsToUse = productAdjustments;
    
    if (productId && window.wickProProgram?.collectionProductAdjustmentsMap) {
      const productAdjustmentsData = window.wickProProgram.collectionProductAdjustmentsMap[productId];
      if (productAdjustmentsData) {
        // Parse if string, or use directly if object
        adjustmentsToUse = typeof productAdjustmentsData === 'string' 
          ? JSON.parse(productAdjustmentsData) 
          : productAdjustmentsData;
      }
    }
    
    if (adjustmentsToUse) {
      let levelAdjustment = null;
      for (const [key, value] of Object.entries(adjustmentsToUse)) {
        const adjustmentKey = utils.normalizeLevelName(key);
        if (adjustmentKey === normalizedProLevel) {
          levelAdjustment = value;
          break;
        }
      }

      // Apply adjustment to base discount
      if (levelAdjustment) {
        if (levelAdjustment.adjustment === 'deny') {
          proDiscount = 0;
        } else if (levelAdjustment.adjustment === 'add') {
          proDiscount = proDiscount + levelAdjustment.value;
        } else if (levelAdjustment.adjustment === 'subtract') {
          proDiscount = Math.max(0, proDiscount - levelAdjustment.value);
        }
      }
    }

    if (proDiscount <= 0) {
      return { discountedPrice: price, discount: 0 };
    }

    // Calculate the discounted price
    const priceForDiscount = compareAt;
    const discountAmount = (priceForDiscount * proDiscount) / 100;
    const discountedPrice = priceForDiscount - discountAmount;
    
    // Only apply if discounted price is less than current price
    const finalPrice = discountedPrice < price ? discountedPrice : price;

    return { 
      discountedPrice: finalPrice, 
      discount: proDiscount,
      originalPrice: price,
      compareAtPrice: compareAt
    };
  }

  /**
   * Update pricing with Pro discounts
   */
  function updateProPricing() {
    // console.log('Updating Pro pricing');
    
    // For product pages, scope to product-info area to avoid cart interference
    const isProductPage = window.location.pathname.includes('/products/') || 
                         document.body.classList.contains('template-product');
    
    let priceScope = document;
    if (isProductPage) {
      // Look for product info container first
      priceScope = document.querySelector('.product-info') || 
                  document.querySelector('[data-block-type="price"]') ||
                  document.querySelector('.product-form') ||
                  document.querySelector('.product-details') ||
                  document;
      // console.log('Product page detected, scoping pricing updates to:', priceScope);
    }
    
    const currentPriceElements = priceScope.querySelectorAll(CONFIG.priceSelectors.current);
    // console.log('Found price elements to update:', currentPriceElements);
    
    currentPriceElements.forEach((element, index) => {
      const elementId = utils.getPriceElementId(element, 'current');
      const originalPrice = parseFloat(element.dataset.originalPrice) || originalPrices.get(elementId);
      
      // Get compare at price if available - also scoped
      const comparePriceElements = priceScope.querySelectorAll(CONFIG.priceSelectors.compare);
      const compareElement = comparePriceElements[index];
      let originalComparePrice = originalPrice;
      
      if (compareElement) {
        const compareElementId = utils.getPriceElementId(compareElement, 'compare');
        originalComparePrice = parseFloat(compareElement.dataset.originalComparePrice) || 
                             originalPrices.get(compareElementId) || 
                             originalPrice;
      }
      
      console.log(`Processing element ${index}: original=${originalPrice}, compare=${originalComparePrice}`);
      
      if (originalPrice > 0) {
        const result = calculateProDiscount(originalPrice, originalComparePrice);
        
        // Determine if there's a sale happening (any discount from compare price)
        const isOnSale = result.discount > 0 || originalComparePrice > originalPrice;
        const finalDisplayPrice = result.discount > 0 ? result.discountedPrice : originalPrice;
        
        // For custom elements, we need to update the text content while preserving screen reader text
        const srText = element.querySelector('.sr-only')?.textContent || '';
        const newPriceText = utils.formatPrice(finalDisplayPrice * 100);
        
        if (srText) {
          element.innerHTML = `<span class="sr-only">${srText}</span>${newPriceText}`;
        } else {
          element.textContent = newPriceText;
        }
        
        // Manage the text-on-sale class
        if (isOnSale) {
          // Add text-on-sale class when there's any kind of sale
          element.classList.add('text-on-sale');
          if (result.discount > 0) {
            element.classList.add(CONFIG.classes.proPrice);
          }
        } else {
          // Remove text-on-sale class when there's no sale
          element.classList.remove('text-on-sale');
          element.classList.remove(CONFIG.classes.proPrice);
        }
        
        // Handle compare-at-price visibility and content
        if (compareElement) {
          if (isOnSale) {
            // Show compare price when there's any sale
            compareElement.removeAttribute('hidden');
            
            // Determine what price to show as compare-at
            let compareDisplayPrice = originalComparePrice;
            
            // If Pro discount is applied, show the higher of original or compare price
            if (result.discount > 0) {
              compareDisplayPrice = Math.max(originalPrice, originalComparePrice);
            }
            
            // Update compare price text
            const compareSrText = compareElement.querySelector('.sr-only')?.textContent || '';
            const comparePriceText = utils.formatPrice(compareDisplayPrice * 100);
            
            if (compareSrText) {
              compareElement.innerHTML = `<span class="sr-only">${compareSrText}</span>${comparePriceText}`;
            } else {
              compareElement.textContent = comparePriceText;
            }
            
            // console.log(`Showing compare price: ${comparePriceText}`);
          } else {
            // Hide compare price when there's no sale
            compareElement.setAttribute('hidden', '');
            // console.log(`Hiding compare price - no sale occurring`);
          }
        }
        
        if (result.discount > 0) {
          // console.log(`Successfully applied Pro discount: ${result.discount}% - Final price: ${newPriceText}`);
        } else if (originalComparePrice > originalPrice) {
          // console.log(`No Pro discount, but regular sale maintained - Price: ${newPriceText}`);
        } else {
          // console.log(`No discount applied - Regular price: ${newPriceText}`);
        }
      } else {
        // console.log(`Skipping element ${index} - no valid original price`);
      }
    });
  }

  /**
   * Add Pro customer badge (single product page)
   */
  function addProBadge() {
    // Only add badge if enabled and customer is active Pro
    if (!badgeSettings.enabled || !customerData.isProActive) {
      // console.log('Badge disabled or customer not active pro');
      return;
    }
 
    // Check if customer is actually getting a discount
    if (!isCustomerGettingDiscount()) {
      console.log('No discount being applied, not showing badge');
      return;
    }

    // Don't add if badge already exists
    if (document.querySelector(`.${CONFIG.classes.proBadge}`)) {
      // console.log('Badge already exists');
      return;
    }
  
    // Find price container - updated for your product page structure
    let priceContainer = document.querySelector('[data-block-type="price"]') ||
                        document.querySelector('price-list') ||
                        document.querySelector('.price-list') ||
                        document.querySelector(CONFIG.priceSelectors.container) ||
                        document.querySelector('.price') ||
                        document.querySelector('.product-price') ||
                        document.querySelector('[data-price]');
  
    // If we found the data-block-type="price", get the price-list inside it
    if (priceContainer && priceContainer.getAttribute('data-block-type') === 'price') {
      const priceListInside = priceContainer.querySelector('price-list');
      if (priceListInside) {
        priceContainer = priceListInside;
      }
    }
  
    if (!priceContainer) {
      // Fallback: create container after first price element
      const firstPrice = document.querySelector('sale-price') || 
                        document.querySelector(CONFIG.priceSelectors.current);
      if (firstPrice) {
        priceContainer = firstPrice.parentElement;
        // console.log('Using fallback price container:', priceContainer);
      }
    }
  
    // console.log('Price container found:', priceContainer);
  
    if (priceContainer) {
      const badge = createProBadge();
      // console.log('Created badge:', badge);
      
      // Position badge based on settings
      if (badgeSettings.position === 'before-price') {
        priceContainer.insertBefore(badge, priceContainer.firstChild);
      } else if (badgeSettings.position === 'above-product') {
        // Find product title and insert before it
        const productTitle = document.querySelector('.product-title, .product__title, h1, .h1, .h2, .h3');
        if (productTitle) {
          productTitle.parentElement.insertBefore(badge, productTitle);
        } else {
          priceContainer.appendChild(badge);
        }
      } else {
        // Default: after price
        priceContainer.appendChild(badge);
      }
      
      // console.log('Badge added to product page');
    } else {
      // console.log('No price container found for badge placement on product page');
    }
  }

  /**
   * Handle variant change events
   */
  function handleVariantChange(event) {
    // console.log('Handling variant change, event:', event);
    
    if (event && window.wickProProgram?.productData?.variants) {
      const selectedVariant = window.wickProProgram.productData.variants.find(v => v.id == event.detail.variant.id);
      // console.log('Selected variant:', selectedVariant);
      
      if (selectedVariant) {
        // Update original price data attributes
        // IMPORTANT: Convert from cents to dollars
        const priceElements = document.querySelectorAll(CONFIG.priceSelectors.current);
        priceElements.forEach(element => {
          element.dataset.originalPrice = selectedVariant.price / 100;
          if (selectedVariant.compare_at_price > 0) {
            element.dataset.originalComparePrice = selectedVariant.compare_at_price / 100;
          }
        });
        
        // Also update compare price elements
        const comparePriceElements = document.querySelectorAll(CONFIG.priceSelectors.compare);
        comparePriceElements.forEach(element => {
          if (selectedVariant.compare_at_price > 0) {
            element.dataset.originalComparePrice = selectedVariant.compare_at_price / 100;
          }
        });
        
        /* console.log('Updated price data attributes for variant:', {
          price: selectedVariant.price / 100,
          compareAtPrice: selectedVariant.compare_at_price / 100
        }); */
      }
    }
    
    // Always try to update pro pricing after a delay
    setTimeout(() => {
      if (customerData.isProActive) {
        storeOriginalPrices();
        updateProPricing();
      }
    }, 200);
  }

  /**
   * Refresh pricing when page content changes (for AJAX loading)
   */
  function refreshPricing() {
    if (!customerData.isProActive) {
      return;
    }

    // Clear processed flags to allow reprocessing
    document.querySelectorAll(`.${CONFIG.classes.processed}`).forEach(element => {
      element.classList.remove(CONFIG.classes.processed);
    });

    // Check if we're on a collection page
    const isCollectionPage = document.body.classList.contains('template-collection') ||
                            document.querySelector('.collection-products') ||
                            document.querySelector('.product-grid') ||
                            window.location.pathname.includes('/collections/');
    
    if (isCollectionPage) {
      processCollectionPage();
    } else {
      storeOriginalPrices();
      updateProPricing();
      addProBadge();
    }
  }

  // Assign core functions to namespace (always update these)
  window.wickProProgram.init = init;
  window.wickProProgram.loadCustomerData = loadCustomerData;
  window.wickProProgram.loadProLevelsData = loadProLevelsData;
  window.wickProProgram.loadProductAdjustments = loadProductAdjustments;
  window.wickProProgram.loadBadgeSettings = loadBadgeSettings;
  window.wickProProgram.updateProPricing = updateProPricing;
  window.wickProProgram.calculateProDiscount = calculateProDiscount;
  window.wickProProgram.addProBadge = addProBadge;
  window.wickProProgram.addProBadgeToCard = addProBadgeToCard;
  window.wickProProgram.createProBadge = createProBadge;
  window.wickProProgram.isCustomerGettingDiscount = isCustomerGettingDiscount;
  window.wickProProgram.isCustomerGettingDiscountForCard = isCustomerGettingDiscountForCard;
  window.wickProProgram.handleVariantChange = handleVariantChange;
  window.wickProProgram.storeOriginalPrices = storeOriginalPrices;
  window.wickProProgram.storeOriginalPricesForCard = storeOriginalPricesForCard;
  window.wickProProgram.updateProPricingForCard = updateProPricingForCard;
  window.wickProProgram.processCollectionPage = processCollectionPage;
  window.wickProProgram.getCurrentVariantId = getCurrentVariantId;
  window.wickProProgram.refreshPricing = refreshPricing;

  // Initialize when DOM is ready (only if first load)
  function initWhenReady() {
    // console.log('Initializing Wick Pro Program...');
    init();
  }

  if (isFirstLoad) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initWhenReady);
    } else {
      // DOM already loaded, initialize with a small delay
      setTimeout(initWhenReady, 100);
    }
    
    // Listen for AJAX content changes (for themes that load content dynamically)
    if (window.MutationObserver) {
      const observer = new MutationObserver((mutations) => {
        let shouldRefresh = false;
        
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // Check if new product cards or price elements were added
            for (let node of mutation.addedNodes) {
              if (node.nodeType === 1) { // Element node
                if (node.matches && (
                  node.matches(CONFIG.priceSelectors.productCard) ||
                  node.querySelector && node.querySelector(CONFIG.priceSelectors.productCard) ||
                  node.matches(CONFIG.priceSelectors.current) ||
                  node.querySelector && node.querySelector(CONFIG.priceSelectors.current)
                )) {
                  shouldRefresh = true;
                  break;
                }
              }
            }
          }
        });
        
        if (shouldRefresh) {
          setTimeout(refreshPricing, 300);
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  } else {
    // Just update functions, don't re-initialize
    // console.log('Functions updated, ready to use');
  }

  // console.log('Wick Pro Program script loaded successfully');

})();